import {AsyncPipe} from '@angular/common'
import {Component, effect, inject, OnInit} from '@angular/core'
import {FormControl, ReactiveFormsModule} from '@angular/forms'
import {MatButton, MatIconButton} from '@angular/material/button'
import {MatFormField, MatLabel} from '@angular/material/form-field'
import {MatIcon} from '@angular/material/icon'
import {MatInput} from '@angular/material/input'
import {getTakeUntilDestroyed} from '@sparbanken-syd/sparbanken-syd-theme'
import {filter, first, switchMap} from 'rxjs/operators'
import {Advice} from '../../model/advice'
import {KalpLoan} from '../../model/kalp-loan'
import {Plural} from '../../model/plural'
import {ControlService} from '../../services/control.service'
import {KalpService, Scenarios} from '../../services/kalp.service'
import {Questions} from '../../services/proposal.service'

@Component({
  selector: 'spb-advice',
  templateUrl: './advice.component.html',
  styleUrls: ['./advice.component.scss'],
  imports: [
    MatButton,
    MatFormField,
    MatLabel,
    MatInput,
    ReactiveFormsModule,
    AsyncPipe,
    MatIcon,
    MatIconButton
  ]
})
export class AdviceComponent extends Plural implements OnInit {
  protected kalpService = inject(KalpService)
  private readonly controlService = inject(ControlService)

  public customAdviceControl = new FormControl()

  private questions: Questions = {
    q3: -1,
    q4: -1,
    q5: -1,
    q6: -1
  }

  private loans: KalpLoan[] = [{} as any]

  private adviceList: Advice[] = [{} as any]

  private scenario: Scenarios = Scenarios.Purchase

  private readonly takeUntilDestroyed = getTakeUntilDestroyed()

  /**
   * Is there surplus in KALP after 2% increase in interest rate?
   * @private
   */
  private twoPercent = 0

  /**
   * Are all loans fixed interest loans or not?
   */
  private fixedOnly = false

  /**
   * If all loan have same terms.
   */
  private allSame = false

  constructor() {
    super()
    effect(() => {
      this.adviceList = this.adviceList.filter(advice => advice.manual)
      this.staticDataService.advice$.next(this.adviceList)
    })
  }

  ngOnInit(): void {
    this.kalpService.scenarioChange$
      .pipe(
        this.takeUntilDestroyed(),
        filter((scenarios: Scenarios | null): scenarios is Scenarios => scenarios !== null),
        switchMap(scenario => {
          this.scenario = scenario
          return this.staticDataService.questions$
        }),
        switchMap(questions => {
          this.questions = questions
          return this.kalpService.kalpResult$
        }),
        switchMap(kalps => {
          this.twoPercent = kalps[2].kalp
          return this.staticDataService.newLoans$
        })
      )
      .subscribe(loans => {
        this.loans = loans
        this.analyze()
      })

    this.staticDataService.advice$
      .pipe(this.takeUntilDestroyed())
      .subscribe(
        advice => this.adviceList = advice
      )
  }

  public addAdvice(): void {
    this.staticDataService.advice$.pipe(
      first()
    ).subscribe(adviceList => {
      adviceList.push({
        advice: this.customAdviceControl.value,
        deleted: false,
        manual: true
      })
      this.customAdviceControl.setValue('', {emitEvent: false})
      this.staticDataService.advice$.next(adviceList)
    })
  }

  public toggleAdvice(advice: Advice): void {
    advice.deleted = !advice.deleted
    this.staticDataService.advice$.pipe(
      first()
    ).subscribe(adviceList => {
      this.staticDataService.advice$.next(adviceList)
    })
  }

  private advicesForNewQuestions(adviceList: Advice[]) {
    const newLoans = this.loans.filter(loan => loan.new)

    // Create variables to calculate the total amount for loans with 3 months and not 3 months.
    let totalAmount3Months = 0
    let totalAmountNot3Months = 0

    // Iterate list of loans
    newLoans.forEach(loan => {
      if (loan.terms === 3) {
        totalAmount3Months += loan.amount
      } else {
        totalAmountNot3Months += loan.amount
      }
    })

    if (newLoans.length === 1) {
      if (newLoans[0].terms === 3) {
        adviceList.push(new Advice(
          `Baserat på hur ${this.plrDuNi.toLowerCase()} bedömer ${this.plrDinEr.toLowerCase()} framtida ekonomi föreslår jag att ` +
          `${this.plrDuNi.toLowerCase()} väljer en 3-mån räntebindning ("rörlig ränta")`))
      } else {
        adviceList.push(new Advice(
          `Baserat på hur ${this.plrDuNi.toLowerCase()} bedömer ${this.plrDinEr.toLowerCase()} framtida ekonomi ` +
          `föreslår jag att ${this.plrDuNi.toLowerCase()} binder räntan på ${this.plrDittErt.toLowerCase()} lån`))
      }
    } else if (totalAmount3Months > 0) {
      if (totalAmount3Months > totalAmountNot3Months) {
        adviceList.push(new Advice(`Baserat på hur ${this.plrDuNi.toLowerCase()} bedömer ` +
          `${this.plrDinEr.toLowerCase()} framtida ekonomi föreslår jag att ${this.plrDuNi.toLowerCase()} väljer ` +
          `övervägande 3-mån räntebindning ("rörlig ränta")`))
      } else {
        adviceList.push(new Advice(`Baserat på hur ${this.plrDuNi.toLowerCase()} bedömer ` +
          `${this.plrDinEr.toLowerCase()} framtida ekonomi föreslår jag att ${this.plrDuNi.toLowerCase()} väljer en ` +
          `blandning av 3-mån räntebindning ("rörlig ränta") och en längre räntebindning`))
      }
    }
  }

  private analyze(): void {
    const manual: Advice[] = this.adviceList.filter((a: Advice) => a.manual)
    const adviceList: Advice[] = []

    this.fixedOnly = this.loans.every((loan: KalpLoan) => loan.terms !== 3)

    if (!this.handleCornerCases(adviceList)) {
      this.advicesForNewQuestions(adviceList)

      this.sortOutTheNumberOfLoans(adviceList)

      if (this.loans.length > 1 && this.twoPercent < 0 && !this.fixedOnly) {
        adviceList.push(new Advice(`Baserat på ${this.plrDinEr.toLowerCase()} ekonomi anser jag att` +
          ` ${this.plrDuNi.toLowerCase()} bör binda delar av lånebeloppet`))
      }

      if (this.twoPercent < 0 && this.fixedOnly) {
        adviceList.push(new Advice(`Med hänsyn till ${this.plrDinEr.toLowerCase()} ekonomi anser jag att` +
          ` ${this.plrDuNi.toLowerCase()} bör binda hela lånet`))
      }

      if (!this.fixedOnly) {
        adviceList.push(new Advice(`Min bedömning är att ${this.plrDinEr.toLowerCase()} ekonomi tål variation i boendekostnaden`))
      }

      if (!this.fixedOnly) {
        adviceList.push(new Advice(`${this.plrDuNi} har möjlighet att extraamortera på rörliga lån`))
      }

    }

    this.adviceList = adviceList.concat(manual)

    this.staticDataService.advice$.next(
      this.adviceList
        .filter((advice: Advice) => advice.manual || !this.controlService.declines()))
  }

  private sortOutTheNumberOfLoans(adviceList: Advice[]): void {
    this.allSame = new Set(this.loans.map(l => l.terms)).size === 1

    if (!this.allSame) {
      adviceList.push(new Advice(
        `Genom att binda på olika bindningstider påverkas ${this.plrDuNi.toLowerCase()} inte lika` +
        ` mycket av ränteläget när de olika bindningstiderna löper ut`))
    }

    // Another special case where number of loans should not be set if the scenario is conditions.
    if (this.scenario !== Scenarios.Conditions) {
      if (this.loans.length === 1) {
        adviceList.push(new Advice(`Med hänsyn tagen till lånets storlek att det är lämpligt att ha det som ett lån`))
      } else {
        adviceList.push(new Advice(`Med hänsyn tagen till lånets storlek är det ` +
          `lämpligt att dela upp det på ${this.loans.length === 2 ? 'två' : 'tre'} lånedelar`))
      }
    }
  }

  private handleCornerCases(adviceList: Advice[]): boolean {
    if (this.questions.q6 === 1) {
      adviceList.push(new Advice(`${this.plrDuNi} planerar att bo i detta boende endast under en kortare period`))
      adviceList.push(new Advice(`${this.plrDuNi} har möjlighet att extraamortera på rörliga lån`))
      return true
    }
    return false
  }
}
